/**
 * Browser-side upload helpers. Mirrors the server's allowlist in
 * packages/core/src/uploads.ts — the server is the source of truth and will
 * reject anything that gets past these checks. We duplicate the small map
 * here rather than importing from @anyangle/core because that package
 * imports node:crypto, which doesn't exist in the browser.
 */

// Mapping from lowercase extension (with dot) to the canonical content type
// the server will accept. Keep in sync with ALLOWED_CONTENT_TYPES in
// packages/core/src/uploads.ts.
export const EXTENSION_CONTENT_TYPE: Record<string, string> = {
  ".step": "model/step",
  ".stp": "model/step",
  ".dxf": "image/vnd.dxf",
  ".dwg": "application/acad",
  ".pdf": "application/pdf",
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
};

export const ALLOWED_EXTENSIONS = Object.keys(EXTENSION_CONTENT_TYPE);

// 50 MB. Larger files would need multipart upload; we keep it simple.
export const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

export function getExtension(filename: string): string {
  const i = filename.lastIndexOf(".");
  return i >= 0 ? filename.slice(i).toLowerCase() : "";
}

export type FileValidationError =
  | { kind: "extension"; ext: string }
  | { kind: "size"; size: number };

export function validateFile(file: File): FileValidationError | null {
  const ext = getExtension(file.name);
  if (!(ext in EXTENSION_CONTENT_TYPE)) return { kind: "extension", ext };
  if (file.size > MAX_FILE_SIZE_BYTES) return { kind: "size", size: file.size };
  return null;
}

/**
 * PUT a file to S3 via XMLHttpRequest so we can report upload progress.
 * `fetch` doesn't expose upload progress in any current browser.
 */
export function putToS3(url: string, file: File, onProgress: (pct: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("content-type", EXTENSION_CONTENT_TYPE[getExtension(file.name)] ?? "application/octet-stream");
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`S3 PUT failed: ${xhr.status}`)));
    xhr.onerror = () => reject(new Error("S3 PUT network error"));
    xhr.send(file);
  });
}
